import { type ReactNode, useEffect } from 'react'
import { useActualTheme, useThemeStore } from '@/stores/themeStore'

interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: 'light' | 'dark' | 'system'
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: ThemeProviderProps) {
  const { theme, setTheme, setSystemTheme } = useThemeStore()
  const actualTheme = useActualTheme()

  // Initialize theme if not set
  useEffect(() => {
    if (!theme) {
      setTheme(defaultTheme)
    }
  }, [theme, defaultTheme, setTheme])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light')
    }

    // Set initial system theme
    setSystemTheme(mediaQuery.matches ? 'dark' : 'light')

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [setSystemTheme])

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement

    root.classList.remove('light', 'dark')
    root.classList.add(actualTheme)
  }, [actualTheme])

  return <>{children}</>
}

// Custom hook for using theme
export function useTheme() {
  const { theme, setTheme } = useThemeStore()
  const actualTheme = useActualTheme()

  return {
    theme,
    actualTheme,
    setTheme,
    toggleTheme: () => {
      setTheme(actualTheme === 'light' ? 'dark' : 'light')
    },
  }
}
